import { useEffect, useRef } from 'react';
import { useAuthStore } from '@/store';

/**
 * useTabSessionGuard — Tüm sekmeler kapatılınca oturumu kapatır.
 *
 * Davranış:
 *  - Kullanıcı login olduğunda store.login() sessionStorage['_tab_alive']='1' set eder.
 *  - Bu sekme yeniden açılırsa sessionStorage BOŞ olur (browser sessionStorage
 *    sekme kapanınca otomatik silinir). O zaman localStorage['_active_tabs']
 *    içinde başka aktif sekmenin heartbeat'ine bakılır:
 *      - VARSA → kullanıcı sadece 1 sekmeyi kapattı, oturum korunur.
 *      - YOKSA → tüm sekmeler kapatılmış → oturum sonlandır (logout).
 *  - Açık kaldığı sürece her 3 sn'de bir heartbeat gönderir.
 *  - `beforeunload`/`pagehide`'da kendi kaydını temizler.
 *
 * Notlar:
 *  - Heartbeat expiry 15 sn: browser çökmesi/force-close durumunda diğer
 *    sekmelerin bu kaydı stale sayıp temizleyebilmesi için.
 *  - Aynı browser context'te birden fazla sekme açıksa, aralarında token
 *    paylaşımı localStorage'daki `auth-storage`(zustand persist) sayesinde
 *    zaten çalışıyor.
 */

const TABS_KEY = '_active_tabs';
const HEARTBEAT_INTERVAL_MS = 3000;
const HEARTBEAT_EXPIRY_MS = 15000;
const TAB_ALIVE_FLAG = '_tab_alive';

function generateTabId() {
  return `${Math.random().toString(36).slice(2, 10)}-${Date.now().toString(36)}`;
}

function readTabs() {
  try {
    return JSON.parse(localStorage.getItem(TABS_KEY) || '{}');
  } catch {
    return {};
  }
}

function writeTabs(tabs) {
  try {
    localStorage.setItem(TABS_KEY, JSON.stringify(tabs));
  } catch {
    /* quota / privacy mode — sessizce yut */
  }
}

function pruneExpired(tabs, now = Date.now()) {
  const fresh = {};
  Object.keys(tabs).forEach((tid) => {
    if (typeof tabs[tid] === 'number' && now - tabs[tid] < HEARTBEAT_EXPIRY_MS) {
      fresh[tid] = tabs[tid];
    }
  });
  return fresh;
}

export function useTabSessionGuard() {
  const { token, isAuthenticated, logout } = useAuthStore();
  const tabIdRef = useRef(null);

  useEffect(() => {
    if (!isAuthenticated || !token) return undefined;

    const isNewTabResurrection = !sessionStorage.getItem(TAB_ALIVE_FLAG);

    if (isNewTabResurrection) {
      // sessionStorage boş → bu sekme browser'a kapatılıp yeniden açıldı
      // Diğer sekmelerden aktif heartbeat var mı?
      const tabs = pruneExpired(readTabs());
      const hasSisterTab = Object.keys(tabs).length > 0;

      if (!hasSisterTab) {
        // Tüm sekmeler kapatılmıştı → oturum bitmiş sayılır
        try { logout(); } catch { /* ignore */ }
        // localStorage tabs kaydını temizle (guvence)
        try { localStorage.removeItem(TABS_KEY); } catch { /* ignore */ }
        if (window.location.pathname !== '/login') {
          setTimeout(() => { window.location.href = '/login'; }, 50);
        }
        return undefined;
      }
      // Kardeş sekme açık → oturum devam eder, bu sekmeyi de aktifleştir
    }

    // Bu sekmeyi "hayatta" olarak işaretle
    sessionStorage.setItem(TAB_ALIVE_FLAG, '1');
    tabIdRef.current = tabIdRef.current || generateTabId();
    const tabId = tabIdRef.current;

    // İlk kayıt + heartbeat
    const registerHeartbeat = () => {
      const cur = pruneExpired(readTabs());
      cur[tabId] = Date.now();
      writeTabs(cur);
    };
    registerHeartbeat();

    const timer = setInterval(registerHeartbeat, HEARTBEAT_INTERVAL_MS);

    const cleanup = () => {
      try {
        const cur = readTabs();
        delete cur[tabId];
        writeTabs(cur);
      } catch { /* ignore */ }
    };

    // Sekme kapatılırken kendini sil (browser force-close olsa da 15 sn
    // sonra diğer tab'lar bunu stale kabul eder)
    window.addEventListener('beforeunload', cleanup);
    window.addEventListener('pagehide', cleanup);

    return () => {
      clearInterval(timer);
      cleanup();
      window.removeEventListener('beforeunload', cleanup);
      window.removeEventListener('pagehide', cleanup);
    };
  }, [isAuthenticated, token, logout]);
}

export default useTabSessionGuard;
